import { useMutation } from '@tanstack/react-query';
import {
  updatePhoneNumber,
  startAuthorizedUserOnboarding,
} from '@Pages/happyFlowPages/phone/api/phone.api.ts';
import { startAccountantOnboarding } from '@Pages/accountantPortalOnboarding/api/accountantPortal.api';
import { useBoundStore } from '@Store/index.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import type { ServerErrorResponse } from '@Types/types.ts';
import type { RegionCode } from 'google-libphonenumber';

interface IUpdatePhoneNumberProps {
  phoneNumber: string;
  isoCode: RegionCode;
}

export const useUpdatePhoneNumber = (
  onError?: (errMsg: string) => void,
  originRoute: RoutesMap = RoutesMap.PHONE
) => {
  const { getCustomerData, setCustomerData } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { onboardingFlowIdentifier } = useBoundStore.getState().onboardingSlice.getOnboardingData();
  const isAuthUserFlow = onboardingFlowIdentifier === OnboardingFlowIdentifier.AUTHORIZED_USER;

  return useMutation({
    mutationFn: async (props: IUpdatePhoneNumberProps) => {
      let phone = props.phoneNumber;

      if (onboardingFlowIdentifier === OnboardingFlowIdentifier.AUTHORIZED_USER_ACCOUNTANT_PORTAL) {
        const authorizedUserId = useBoundStore.getState().authOnboardingSlice.authorizedUserId!;
        await startAccountantOnboarding(authorizedUserId, {
          phoneNumber: props.phoneNumber,
          phoneNumberCountryCode: props.isoCode,
        });
      } else {
        phone = await updatePhoneNumber(props.phoneNumber, props.isoCode);
        if (isAuthUserFlow) {
          await startAuthorizedUserOnboarding({
            phoneNumber: props.phoneNumber,
            phoneNumberCountryCode: props.isoCode,
          });
        }
      }
      return phone;
    },
    onSuccess: (updatedPhone: string) => {
      const customerDataToUpdate = getCustomerData();
      customerDataToUpdate.customerData.phoneNumber = updatedPhone;
      setCustomerData(customerDataToUpdate);
      navigateToNextPage(originRoute);
    },
    onError: (error: ServerErrorResponse) => {
      if (onError) {
        onError(error?.response?.data?.errors?.[0]?.localizedMessage || '');
      }
    },
  });
};
